import React, {useState, useEffect, useRef, useCallback} from "react";
import { Tooltip } from "react-tooltip";

import 'react-tooltip/dist/react-tooltip.css';
import '../../css/common-components.css';
import '../../css/common-styles.css';

import styles from './AddCC.module.css';

const MAX_CC_RECIPIENTS = 20;

const AddCC = ({
    recipients = [],
    errors = {},
    setErrors,
    showToaster,
    contactSuggestions = [],
    enableSuggestions = false,
    initialCcEmails = [],
    lockedEmails = [],
    onCcChange
  }) => {

    const [ccEmailTags, setCcEmailTags] = useState([]);
    const [ccInputValue, setCcInputValue] = useState("");

    const [editingTagIndex, setEditingTagIndex] = useState(null);
    const [editingTagValue, setEditingTagValue] = useState("");

    const [focusedInputId, setFocusedInputId] = useState(null);
    const [duplicateTags, setDuplicateTags] = useState([]);
    const [filteredContactSuggestions, setFilteredContactSuggestions] = useState([]);

    const editingTagRef = useRef(null);
    const initialized = useRef(false);
    const previousInitRef = useRef("");

    useEffect(() => {
      const emailsJson = JSON.stringify(initialCcEmails || []);

      if (previousInitRef.current === emailsJson) {
        return;
      }

      previousInitRef.current = emailsJson;

      if (initialCcEmails?.length > 0) {
        setCcEmailTags(initialCcEmails);
      }
      
    }, [initialCcEmails]);

    useEffect(() => {    
      if (!initialized.current) {
        initialized.current = true;
        return;
      }

      onCcChange?.(ccEmailTags);
    }, [ccEmailTags, onCcChange]);

    const isValidEmail = (email) => {
      const re = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

      return re.test(email);
    };

    const ensureCcCapacity = (countToAdd = 1) => {
      const available = MAX_CC_RECIPIENTS - ccEmailTags.length;
      if (countToAdd > available) {
        showToaster?.(`Maximum CC recipient count should be less than or equal to ${MAX_CC_RECIPIENTS}`,"error");
        return false;
      }
      return true;
    };

    const getRecipientEmails = () => {
      return recipients.map(r => (r.email ||r.signerEmail ||"").trim().toLowerCase()).filter(Boolean);
    };

    const clearCcDuplicateErrors = useCallback((nextTags) => {
      if (!setErrors) return;

      const ccSet = new Set(nextTags.map(x => x.toLowerCase()));

      setErrors(prev => {
        const updated = [...(prev.recipients || [])];
        recipients.forEach((recipient, idx) => {
            const email = (recipient.email ||recipient.signerEmail ||"").toLowerCase();
            if (updated[idx]?.email ==="Recipient already exists in CC" &&!ccSet.has(email)) {
              updated[idx] = {
                ...updated[idx],
                email: ""
              };
            }
            if (updated[idx]?.signerEmail ==="Recipient already exists in CC" &&!ccSet.has(email)) {
              updated[idx] = {
                ...updated[idx],
                signerEmail: ""
              };
            }
        });

        return {
            ...prev,
            recipients: updated
        };
      });
    },[recipients, setErrors]);

    const filterContactSuggestions = useCallback((searchText) => {

      if (!enableSuggestions) return;

      if (!searchText?.trim()) {
        setFilteredContactSuggestions(contactSuggestions);
        return;
      }

      const value = searchText.toLowerCase();
      const filtered = contactSuggestions.filter(contact =>
        contact.name?.toLowerCase().includes(value) ||
        contact.email?.toLowerCase().includes(value)
      );
      setFilteredContactSuggestions(filtered);
      },
      [contactSuggestions, enableSuggestions]
    );

    const addEmail = (email) => {
        const normalized = email.trim();

        if (!isValidEmail(normalized))
            return;

        const recipientEmails = getRecipientEmails();

        const duplicateInCC =ccEmailTags.some(e => e.toLowerCase() === normalized.toLowerCase());

        const duplicateRecipient = recipientEmails.includes(normalized.toLowerCase());

        if (duplicateRecipient) {
          return;
        }

        if (duplicateInCC) {

    const duplicateTag =
        `${normalized}_duplicate_${Date.now()}`;

    setDuplicateTags(prev => [
        ...prev,
        duplicateTag
    ]);

    setTimeout(() => {
        setDuplicateTags(prev =>
            prev.filter(
                tag => tag !== duplicateTag
            )
        );
    }, 500);

    return;
}

        if (!ensureCcCapacity(1))
          return;

        const updated = [...ccEmailTags, normalized];

        setCcEmailTags(updated);

        clearCcDuplicateErrors(updated);
    };

    const handleCcInputChange = value => {

        setCcInputValue(value);

        if (value.includes(",")) {
            const emails = value.split(",").map(e => e.trim()).filter(Boolean);

            const completed = emails.slice(0, -1);

            completed.forEach(addEmail);

            setCcInputValue(emails[emails.length - 1] || "");
        }

        filterContactSuggestions(value.split(",").pop()?.trim() || "");
    };

    const handleCcInputKeyPress = e => {
      if (e.key === "Enter" ||e.key === ",")
      {
        e.preventDefault();
        if (ccInputValue.trim())
        {
          addEmail(ccInputValue.trim());
          setCcInputValue("");
        }
      }

      if (e.key === "Backspace" &&!ccInputValue &&ccEmailTags.length)
      {
        const lastEmail = ccEmailTags[ccEmailTags.length - 1];
        if (lockedEmails.includes(lastEmail))
        {
          return;
        }
        setCcEmailTags(
            prev => prev.slice(0, -1)
        );
      }
    };

    const applyCcSuggestion = contact => {
        if (!contact) return;

        addEmail(contact.email);

        setCcInputValue("");
        setFocusedInputId(null);
    };

    const removeCcEmailTag = email => {

      if (lockedEmails.includes(email)) {
        return;
      }

      const updated = ccEmailTags.filter(x => x !== email);

      setCcEmailTags(updated);
      clearCcDuplicateErrors(updated);
    };

    const startEditingCcTag = index => {
      if (lockedEmails.includes(ccEmailTags[index]))
      {
        return;
      }

      setEditingTagIndex(index);
      setEditingTagValue(ccEmailTags[index]);
    };

    const finishEditingCcTag = () => {
      if (editingTagIndex === null)
          return;
      if (lockedEmails.includes(ccEmailTags[editingTagIndex]))
      {
        setEditingTagIndex(null);
        return;
      }
      const value = editingTagValue.trim();
      if (!value) {
        removeCcEmailTag(ccEmailTags[editingTagIndex]);
      }
      else if (isValidEmail(value))
      {
        const updated = [...ccEmailTags];
        updated[editingTagIndex] = value;
        setCcEmailTags(updated);
      }
      setEditingTagIndex(null);
      setEditingTagValue("");
    };

    const getEditingInputWidth =
        value => `${Math.max(value.length * 8,80)}px`;

    const isDuplicateEmail = (email, index) => {
      const first = ccEmailTags.indexOf(email);

      const last = ccEmailTags.lastIndexOf(email);

      return (first !== last && index === last);
    };

    useEffect(() => {
      const handleClickOutside = event => {
        if (editingTagIndex !== null && editingTagRef.current && !editingTagRef.current.contains(event.target))
        {
          finishEditingCcTag();
        }
      };
      document.addEventListener("mousedown",handleClickOutside);

      return () => document.removeEventListener("mousedown",handleClickOutside);
    }, [editingTagIndex]);

    const handleCcWrapperClick = (e) => {
        if (e.target.closest(`.${styles.ccEmailTag}`) ||editingTagIndex !== null)
        {
          return;
        }
      
        const input = document.querySelector(".cc-text-input");
      
        if (input) {
          input.focus();
        }
    };

    const handleCcTagEditingKeyPress = (e) => {
    
        if (e.key === "Enter") {
            e.preventDefault();
            finishEditingCcTag();
        }
      
        if (e.key === "Escape") {
            setEditingTagIndex(null);
            setEditingTagValue("");
        }
    };
    const handleCcInputBlur = () => {
    
        const email = ccInputValue.trim();
    
        if (email &&isValidEmail(email))
        {
          addEmail(email);
          setCcInputValue("");
        }
      
        setTimeout(() => {
          setFocusedInputId(null);
        }, 150);
    };

    return (
        <div className="contentBox">
            <div className="contentBoxHeader">
                <span>Add CC</span>
                <span
                    className={`${styles.iconBase} ${styles.infoIcon} sfIconBase sfInfoIcon`}
                    data-tooltip-id="cc-tooltip"
                />
                <Tooltip
                    id="cc-tooltip"
                    place="top"
                    className="tooltip-info tip-CC"
                    render={() => (
                      <div className="tooltip-content">
                          CC recipients will receive a copy of the completed document. They will also be notified of important document progress-related events.
                      </div>
                    )}
                />
            </div>
          <div className="cc-input-container" style={{ position: 'relative' }}>
            <div className={`${styles.ccInputWrapper} ${errors.cc ? 'error' : ''}`} onClick={handleCcWrapperClick} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleCcWrapperClick(e); }}>
              {[...ccEmailTags, ...duplicateTags].map((email, index) => (
                <div key={`${email}-${index}`} className={`${styles.ccEmailTag} ${email.includes("_duplicate_") ? styles.ccEmailTagDuplicate : '' }`}>
                  {editingTagIndex === index ? (
                    <input
                      ref={editingTagRef}
                      type="text"
                      className={styles.ccEmailEditInput}
                      value={editingTagValue}
                      onChange={(e) => setEditingTagValue(e.target.value)}
                      onKeyDown={handleCcTagEditingKeyPress}
                      onBlur={finishEditingCcTag}
                      style={{ 
                        width: getEditingInputWidth(editingTagValue),
                        minWidth: '80px'
                      }}
                    />
                  ) : (
                    <>
                      <span 
                        className={styles.ccEmailText}
                        onDoubleClick={() => startEditingCcTag(index)}
                        style={{ cursor: 'text' }}
                      >
                        {email.includes("_duplicate_") ? email.split("_duplicate_")[0] : email}
                      </span>
                      {!lockedEmails.includes(email) && (
                        <button
                            type="button"
                            className={styles.ccEmailRemove}
                            onClick={(e) => {
                                e.stopPropagation();
                                removeCcEmailTag(email);
                            }}
                        >
                        </button>
                      )}
                    </>
                  )}
                </div>
              ))}
              <input
                type="text"
                className={`${styles.ccTextInput} cc-text-input`}
                placeholder={ccEmailTags.length === 0 ? "Enter one or more email addresses separated by comma" : ""}
                value={ccInputValue}
                onChange={(e) => handleCcInputChange(e.target.value)}
                onKeyDown={handleCcInputKeyPress}
                onFocus={() => {
                  setFocusedInputId('cc-input');
                  filterContactSuggestions(ccInputValue);
                }}
                onBlur={handleCcInputBlur}
              />
            </div>
            {focusedInputId === 'cc-input' && filteredContactSuggestions.length > 0 && (
              <div className="contact-suggestions-container">
                {filteredContactSuggestions.map((contact, contactIndex) => (
                  <div 
                    key={contactIndex}
                    className="contact-suggestion-box" role="button" tabIndex={0} onKeyDown={(e) => {if (e.key === "Enter" || e.key === " ") applyCcSuggestion(contact);}}
                    onMouseDown={(e) => {
                      // Prevent blur event from firing before click
                      e.preventDefault();
                      applyCcSuggestion(contact);
                    }}
                  >
                    <div className="contact-name">{contact.name}</div>
                    <div className="contact-email">{contact.email}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {errors.cc && <div className="error-message">{errors.cc}</div>}
        </div>
    );
};

export default AddCC;